import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const SentVerses = ({ discipleId, userAppID, onSentVersesUpdate }) => {
  const [allMessages, setAllMessages] = useState([]);
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [mountKey, setMountKey] = useState(0);

  const toggleMessageExpand = (messageId) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const fetchSentVerses = async () => {
    try {
      if (!userAppID) return;

      // First, get all verses data to use as a lookup
      const versesSnapshot = await getDocs(collection(db, 'verses'));
      const versesMap = {};
      versesSnapshot.docs.forEach(doc => {
        const verseData = doc.data();
        versesMap[verseData.reference] = {
          growthArea: verseData.growthArea || '',
          devTopic: verseData.devTopic || '',
          concept: verseData.concept || '',
          url: verseData.url || '',
          tags: verseData.tags || [],
          categories: verseData.categories || []
        };
      });

      // Fetch both email and SMS messages
      const [emailSnapshot, smsSnapshot] = await Promise.all([
        getDocs(collection(db, 'disciples', discipleId, 'mentorDetails', userAppID, 'messages')),
        getDocs(collection(db, 'disciples', discipleId, 'mentorDetails', userAppID, 'messagesSMS'))
      ]);

      // Create a map to combine messages by verse reference
      const messagesByVerse = new Map();

      // Process email messages
      emailSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!data.placeholder && data.verseReference) {
          const verseData = versesMap[data.verseReference] || {};
          const key = data.verseReference;
          
          if (!messagesByVerse.has(key)) {
            messagesByVerse.set(key, {
              ...data,
              ...verseData,
              id: doc.id,
              sentVia: new Set(['email']),
              emailData: data,
              sentAt: data.sentAt || data.createdAt,
            });
          }
        }
      });

      // Process SMS messages
      smsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!data.placeholder && data.verseReference) {
          const verseData = versesMap[data.verseReference] || {};
          const key = data.verseReference;
          
          if (messagesByVerse.has(key)) {
            // Combine with existing message
            const existingMessage = messagesByVerse.get(key);
            existingMessage.sentVia.add('sms');
            existingMessage.smsData = data;
            // Use the most recent sent date
            const smsDate = data.sentAt || data.createdAt;
            if (smsDate?.seconds > existingMessage.sentAt?.seconds) {
              existingMessage.sentAt = smsDate;
            }
          } else {
            // Create new message entry
            messagesByVerse.set(key, {
              ...data,
              ...verseData,
              id: doc.id,
              sentVia: new Set(['sms']),
              smsData: data,
              sentAt: data.sentAt || data.createdAt,
            });
          }
        }
      });

      // Convert map to array and sort by date
      const combinedMessages = Array.from(messagesByVerse.values())
        .map(message => ({
          ...message,
          sentVia: Array.from(message.sentVia)
        }))
        .sort((a, b) => (b.sentAt?.seconds || 0) - (a.sentAt?.seconds || 0));

      setAllMessages(combinedMessages);
      
      // Notify parent component of sent verses
      if (onSentVersesUpdate) {
        onSentVersesUpdate(combinedMessages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (discipleId && userAppID) {
      fetchSentVerses();
    }
  }, [discipleId, userAppID, mountKey]);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Latest Messages</h2>
        <div className="space-y-4">
          {isLoading ? (
            <p className="text-center text-gray-500">Loading messages...</p>
          ) : (
            <>
              {allMessages.map((message) => (
                <div key={message.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium">{message.verseReference}</h3>
                      <p className="text-sm text-gray-600">{message.text}</p>
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      <button
                        onClick={() => toggleMessageExpand(message.id)}
                        className="text-sm text-[#0076BE] hover:text-[#005991]"
                      >
                        {expandedMessages.has(message.id) ? 'Hide' : 'Details'}
                      </button>
                      <span className="text-xs text-gray-500">
                        {message.sentAt?.toDate().toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {expandedMessages.has(message.id) && (
                    <div className="mt-3 pt-3 border-t">
                      {message.question && (
                        <div className="mb-3">
                          <span className="text-sm font-medium">Question: </span>
                          <span className="text-sm text-gray-600">{message.question}</span>
                        </div>
                      )}
                      
                      {message.customMessage && (
                        <div className="mb-3">
                          <span className="text-sm font-medium">Your Message: </span>
                          <span className="text-sm text-gray-600">{message.customMessage}</span>
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-2">
                        {message.growthArea && (
                          <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full">
                            {message.growthArea}
                          </span>
                        )}
                        {message.devTopic && (
                          <span className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded-full">
                            {message.devTopic}
                          </span>
                        )}
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                          Sent via {message.sentVia.join(' & ')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {allMessages.length === 0 && (
                <p className="text-gray-500 text-center py-4">No messages sent yet</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SentVerses; 